import { ComponentsManager, Component, EventBus } from '../core/smpl-components.js'
import { StickyHeader } from '../modules/sticky-header.js'
import { OverlayInteractionListener } from '../utils/overlay-interaction-listener.js'
import { Eyes } from '../modules/eyes.js'
import { LogoBounce } from '../modules/logo-bounce.js'
import { gsap } from 'gsap'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

/** Overlay menu
 *
 * Drives the fullscreen main-nav overlay (`.js-nav-overlay`). CSS owns the
 * layout at all times — the panel is fixed to the viewport with its items
 * centred — and its resting state is hidden (`visibility:hidden; opacity:0`).
 * GSAP only drives the transition: the background fades in first, then the menu
 * items stagger up underneath it.
 *
 * On close-complete every inline prop GSAP wrote is cleared, so the hidden
 * state falls back to CSS and window-resize needs no JS at all.
 */
// How far below its place each half of the wordmark starts when the overlay
// reveals, in SVG user units (about 40% of the 29-unit-tall logo).
const LOGO_REVEAL_OFFSET = 8

// How far below its place the toggle's word rests, in SVG user units (the word
// itself is ~14 tall in a 74x90 viewBox).
const TOGGLE_TEXT_TRAVEL = 8

// How far the toggle is knocked down, in px, as the closing overlay drops away.
const TOGGLE_CLOSE_BOUNCE = 10

// How far the toggle kicks up, in px, the moment it's clicked to open.
const TOGGLE_OPEN_BOUNCE = 10

// How long the panel waits after the click before it springs up, in seconds,
// so the toggle's hop plays in full view first. 0.12 is when the hop peaks;
// 0 opens the panel instantly (the hop is then mostly hidden behind it).
const OPEN_DELAY = 0.02

class OverlayMenu {
	constructor(el, options) {
		this.element = el // .js-main-nav (component root)
		this.options = Object.assign({}, options)

		this.elOverlay = this.element.querySelector('.js-nav-overlay')
		this.elToggle = this.element.querySelector('.js-toggle')
		this.elClose = this.element.querySelector('.js-nav-overlay-close')
		// The menu row is the overlay's only scroller (the logo and the
		// character stay put around it).
		this.elBody = this.elOverlay.querySelector('.nav-overlay__body')

		// Stagger targets: the inner span of every menu link (the span is the
		// transform handle, so the link keeps its own hit area) + the language
		// pill trailing the list.
		this.elItems = [...this.elOverlay.querySelectorAll('.menu-item__link > span'), ...this.elOverlay.querySelectorAll('.nav-overlay__language')]

		// The wordmark: its two halves bounce in one after the other on reveal,
		// while on close the logo leaves as one piece.
		this.elLogo = this.elOverlay.querySelector('.nav-overlay__header-link')
		this.elLogoBoeken = this.elOverlay.querySelector('.logo__boeken')
		this.elLogoBerg = this.elOverlay.querySelector('.logo__berg')

		this.isVisible = false
		this.scrollLocked = false
		this.tl = null

		this.bndToggle = this.toggle.bind(this)
		this.bndOnToggleEnter = this.onToggleEnter.bind(this)
		this.bndOnToggleLeave = this.onToggleLeave.bind(this)
		this.bndOnOverlayClick = this.onOverlayClick.bind(this)

		// Escape only — the overlay covers the viewport, so there is no
		// "outside" left to click; background clicks are handled below.
		this.interactionListener = new OverlayInteractionListener({
			element: this.element,
			keys: ['Escape'],
			enabled: false,
			onKeyPress: () => this.hide(),
		})

		this.elOverlay.setAttribute('aria-hidden', 'true')
		this.elToggle.setAttribute('aria-expanded', 'false')

		// The close button's character follows the cursor while the overlay is
		// open, and looks up cross-eyed while the button itself is hovered. It is
		// only started on show: the panel is hidden at rest, so there is nothing
		// to measure and nothing worth ticking for.
		this.eyes = new Eyes(this.elClose, { lookUpOnHover: true, follow: false })

		// The toggle's own character. It tracks the cursor around the page and
		// looks up while the toggle is hovered, where the word above it springs
		// in. Always running: the bar is on screen the whole time.
		this.toggleEyes = new Eyes(this.elToggle, { lookUpOnHover: true, follow: false })
		this.toggleEyes.start()

		// The word rests below its place and hidden; hovering springs it up. Only
		// on pointers that can hover — on touch CSS leaves it visible.
		this.elToggleText = this.element.querySelector('.nav-bar__toggle-text')
		if (this.elToggleText && window.matchMedia('(hover: hover)').matches) {
			gsap.set(this.elToggleText, { y: TOGGLE_TEXT_TRAVEL, autoAlpha: 0 })
			this.elToggle.addEventListener('pointerenter', this.bndOnToggleEnter)
			this.elToggle.addEventListener('pointerleave', this.bndOnToggleLeave)
		}

		// Console handles, next to `eyes` for the character on the home page.
		if (this.options.debug) {
			window.navEyes = this.eyes
			window.toggleEyes = this.toggleEyes
		}

		this.elToggle.addEventListener('click', this.bndToggle)
		this.elOverlay.addEventListener('click', this.bndOnOverlayClick)
		this.elClose.addEventListener('click', this.bndOnOverlayClick)
	}

	toggle(e) {
		if (this.isVisible) {
			this.hide(e)
		} else {
			this.show(e)
		}
	}

	show(e) {
		if (this.isVisible) return
		if (e) {
			e.preventDefault()
			e.stopPropagation()
		}
		this.isVisible = true

		if (this.tl) this.tl.kill()

		if (this.options.onToggle) this.options.onToggle(true)

		// `is-expanded` is the instant toggle state (it morphs the burger into
		// a cross); `is-overlay-active` tracks the panel itself and outlives it
		// until the close animation is done.
		this.element.classList.add('is-expanded', 'is-overlay-active')
		this.elOverlay.setAttribute('aria-hidden', 'false')
		this.elToggle.setAttribute('aria-expanded', 'true')

		// The overlay covers the viewport, so the page behind it must not
		// scroll. Pass the menu row, the element that actually scrolls: on iOS
		// the lock checks the scroll edges of exactly this element, so handing it
		// a non-scrolling parent would block every touch-scroll in the menu.
		if (!this.scrollLocked) {
			this.scrollLocked = true
			window.app.browser.disableScroll(this.elBody)
		}

		this.elBody.scrollTop = 0

		// The toggle kicks up the moment it's clicked, and the panel waits
		// OPEN_DELAY before springing up, so the hop is seen before the panel
		// covers it.
		this.bounceToggle(-TOGGLE_OPEN_BOUNCE, 'power2.out')

		this.eyes.start()

		// The fromTo's below still render their start state (hidden) right away,
		// so nothing flashes during the delay.
		this.tl = gsap.timeline({ delay: OPEN_DELAY })
		this.tl.fromTo(
			this.elOverlay,
			{ autoAlpha: 0, yPercent: 5, scaleY: 0.9 },
			{ autoAlpha: 1, yPercent: 0, scaleY: 1, duration: 0.5, transformOrigin: 'bottom', ease: 'elastic.out(2,1.72)' },
		)
		this.tl.fromTo(this.elClose, { yPercent: 10 }, { yPercent: 0, duration: 0.5, transformOrigin: 'bottom', ease: 'elastic.out(2,1.72)' }, 0.1)
		this.tl.fromTo(
			this.elItems,
			{ autoAlpha: 0, yPercent: 30 },
			{
				autoAlpha: 1,
				yPercent: 0,
				duration: 0.7,
				ease: 'elastic.out(2,0.75)',
				stagger: 0.06,
			},
			0.1,
		)

		// The wordmark bounces in with the items: "boeken" first on the items'
		// spring, then "berg" a beat later on a looser one, so it lands with an
		// extra wobble of its own.
		if (this.elLogoBoeken && this.elLogoBerg) {
			this.tl.fromTo(this.elLogoBoeken, { autoAlpha: 0, y: LOGO_REVEAL_OFFSET }, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'elastic.out(2,0.75)' }, 0.1)
			this.tl.fromTo(this.elLogoBerg, { autoAlpha: 0, y: LOGO_REVEAL_OFFSET }, { autoAlpha: 1, y: 0, duration: 1, ease: 'elastic.out(1.2,0.35)' }, 0.25)
		}

		this.interactionListener.enable()
	}

	hide(e) {
		if (!this.isVisible) return
		if (e) {
			e.preventDefault()
			e.stopPropagation()
		}
		this.isVisible = false

		// The panel drops away, so the character watches it go. Held until the
		// eyes stop at the end of the timeline.
		this.eyes.lookDown()

		if (this.tl) this.tl.kill()

		if (this.options.onToggle) this.options.onToggle(false)

		// Flip the burger back the moment the close starts — it reads as the
		// cause of the panel leaving, not as a report that it has left.
		this.element.classList.remove('is-expanded')
		this.elToggle.setAttribute('aria-expanded', 'false')

		this.tl = gsap.timeline({
			onComplete: () => {
				this.element.classList.remove('is-overlay-active')
				this.elOverlay.setAttribute('aria-hidden', 'true')
				// Drop every inline prop so the CSS resting state (hidden)
				// takes over again.
				gsap.set([this.elOverlay, this.elLogo, ...this.elItems].filter(Boolean), { clearProps: 'all' })
				if (this.elLogoBoeken && this.elLogoBerg) gsap.set([this.elLogoBoeken, this.elLogoBerg], { clearProps: 'all' })
				this.eyes.stop()
				if (this.scrollLocked) {
					this.scrollLocked = false
					window.app.browser.enableScroll()
				}
			},
		})
		this.tl.to(this.elClose, { yPercent: 10, duration: 0.4, transformOrigin: 'bottom', ease: 'back.in(5)' })
		// The logo leaves as one piece, first in the items' stagger, so it reads
		// as the start of the same upward wave.
		this.tl.to(
			[this.elLogo, ...this.elItems].filter(Boolean),
			{
				autoAlpha: 0,
				yPercent: -30,
				duration: 0.25,
				ease: 'power2.in',
				stagger: 0.02,
			},
			0.1,
		)

		this.tl.to(this.elOverlay, { autoAlpha: 0, yPercent: 2, scaleY: 0.9, duration: 0.4, transformOrigin: 'bottom', ease: 'back.in(3)' }, 0.1)

		// The panel's drop accelerates into its last 0.1s; that is when the toggle
		// gets knocked down with it. Only triggered from the timeline, not part
		// of it: the spring outlasts the close, and stretching the timeline would
		// hold the scroll lock and the reset in onComplete back until it settles.
		this.tl.call(() => this.bounceToggle(TOGGLE_CLOSE_BOUNCE), null, 0.4)

		this.interactionListener.disable()
	}

	// Springs the word up into place. `overwrite` keeps a fast in-out from
	// stacking two tweens on the same transform.
	onToggleEnter() {
		gsap.to(this.elToggleText, { y: 0, autoAlpha: 1, duration: 0.7, ease: 'elastic.out(1, 0.45)', overwrite: true })
	}

	onToggleLeave() {
		gsap.to(this.elToggleText, { y: TOGGLE_TEXT_TRAVEL, autoAlpha: 0, duration: 0.25, ease: 'power2.in', overwrite: true })
	}

	// A quick push by `offset` px (positive is down), then an elastic spring
	// back into place. Shared by open (kick up) and close (knocked down).
	bounceToggle(offset, ease = 'power2.in') {
		gsap.to(this.elToggle, {
			keyframes: [
				{ y: offset, duration: 0.12, ease },
				{ y: 0, duration: 0.7, ease: 'elastic.out(1,2)' },
			],
			overwrite: 'auto',
			clearProps: 'transform',
		})
	}

	onOverlayClick(e) {
		// The close button lives inside the overlay, so its click lands here too.
		if (e.target.closest('.js-nav-overlay-close')) {
			this.hide(e)
			return
		}

		// Otherwise only a link closes the overlay (Swup handles the navigation
		// itself). Clicking the background does nothing — use the close button,
		// the burger or Escape.
		const link = e.target.closest('a')
		if (link && link.href) this.hide()
	}

	destroy() {
		if (this.tl) this.tl.kill()
		gsap.killTweensOf(this.elToggle)
		this.eyes.destroy()
		this.toggleEyes.destroy()
		this.elToggle.removeEventListener('pointerenter', this.bndOnToggleEnter)
		this.elToggle.removeEventListener('pointerleave', this.bndOnToggleLeave)
		this.elToggle.removeEventListener('click', this.bndToggle)
		this.elOverlay.removeEventListener('click', this.bndOnOverlayClick)
		this.interactionListener.destroy()
		if (this.scrollLocked) {
			this.scrollLocked = false
			window.app.browser.enableScroll()
		}
	}
}

class OverlayHandler {
	constructor(element, options) {
		this.overlayMenu = new OverlayMenu(element, {
			...options,
			onToggle: (state) => {},
		})
	}

	hide() {
		this.overlayMenu.hide()
	}

	show() {
		this.overlayMenu.show()
	}

	destroy() {
		this.overlayMenu.destroy()
	}
}

/** Language toggle
 *
 * The pill is a single link to the other language. On click we don't prevent
 * navigation — we just flip the active index so the knob slides to the other
 * side during the page transition.
 */
class LanguageToggle {
	constructor(element) {
		this.element = element
		this.bndOnClick = this.onClick.bind(this)
		this.element.addEventListener('click', this.bndOnClick)
	}
	onClick() {
		const current = Number(this.element.dataset.active || 0)
		this.element.dataset.active = current === 0 ? 1 : 0
	}
	destroy() {
		this.element.removeEventListener('click', this.bndOnClick)
	}
}

//
export default class extends Component {
	prepare() {
		// -- Foldout / burger toggle -------------------------- //
		this.overlayHandler = new OverlayHandler(this.element, this.options)

		// -- Logo ---------------------------------------------- //
		this.navLogo = new LogoBounce(this.element.querySelector('.nav-overlay__header-link'))

		// // -- Sticky Header ------------------------------------ //
		this.stickyHeader = new StickyHeader({
			element: this.element,
			offset: 20,
			showAtBottom: false,
			thresholds: { reposition: 100 },
			on: {
				stateChange: (newState, oldState, ctx) => {
					// console.log(`Header state: ${oldState} → ${newState}`)
					// document.body.classList.remove(`header-state-${oldState}`)
					// document.body.classList.add(`header-state-${newState}`)
				},
				'state:top': (ctx) => {},
				'state:hidden': (ctx) => {
					this.overlayHandler.hide()
				},
				'threshold:compact': ({ crossed }) => {},
				'threshold:cta': ({ crossed }) => {},
			},
		})
	}

	mount() {}

	unmount() {
		if (this.overlayHandler) {
			this.overlayHandler.destroy()
		}

		if (this.navLogo) {
			this.navLogo.destroy()
		}

		if (this.languageToggle) {
			this.languageToggle.destroy()
		}

		if (this.stickyHeader) {
			this.stickyHeader.destroy()
		}
	}

	onSwupNavSelfClick(visit) {
		if (this.navIsland) {
			this.navIsland.collapse()
		}
	}
	onNavShapeClick(e) {
		// if (this.overlayMenu.isVisible) return
		// this.overlayMenu.show()
	}
}
